import { Fragment, useEffect, useState } from 'react';
import { api } from '../api.js';
import { decorateTeam, handoutsByTeam, kr, officeDays, officeStats, seasonTotals, sortTeams, teamHandouts, teamTypes, yearStats } from '../stats.js';
import { Bar, Kpi, NumberInput, PlayerTable, SortableList, YearSelect } from '../components/ui.jsx';
import { readPlayerNames } from '../xlsx.js';

const TABS = [
  { id: 'overview', label: 'Översikt', icon: '◧' },
  { id: 'teams', label: 'Lag & spelare', icon: '◎' },
  { id: 'office', label: 'Kansliet', icon: '◈' },
  { id: 'settings', label: 'Inställningar', icon: '⚙' },
];

function useNarrow() {
  const [narrow, setNarrow] = useState(() => window.innerWidth < 900);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
}

export function Admin({ data, year, setYear, actions }) {
  const narrow = useNarrow();
  const [tab, setTab] = useState(data.years.length ? 'overview' : 'settings');
  const [detailId, setDetailId] = useState(null);

  const season = year != null ? data.seasons[year] : null;
  const dteams = season ? season.teams.map(t => decorateTeam(t, season)) : [];
  const detail = dteams.find(t => t.id === detailId) || null;
  const title = TABS.find(t => t.id === tab)?.label || '';

  return (
    <div className="admin">
      {!narrow && (
        <aside className="side">
          <div className="brand">
            <div className="logo"><img src="/sif-logo.png" alt="SIF" /></div>
            <div>
              <div style={{ fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.1, color: '#fff' }}>Sandareds IF</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', fontWeight: 600 }}>Administration</div>
            </div>
          </div>
          <nav>
            {TABS.map(t => (
              <button key={t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>
                <span className="ico">{t.icon}</span>{t.label}
              </button>
            ))}
          </nav>
          {data.years.length > 0 && (
            <div style={{ padding: '0 4px', marginBottom: 12 }}>
              <label className="season-label">Säsong</label>
              <YearSelect years={data.years} year={year} onChange={setYear} className="" />
            </div>
          )}
          <button className="logout" onClick={actions.logout}>Logga ut</button>
        </aside>
      )}

      <main>
        {narrow ? (
          <div className="mobile-top">
            <div className="row">
              <img src="/sif-logo.png" alt="SIF" style={{ width: 30, height: 30, objectFit: 'contain' }} />
              <div style={{ fontWeight: 800, flex: 1 }}>Sandareds IF</div>
              {data.years.length > 0 && <YearSelect years={data.years} year={year} onChange={setYear} />}
              <button className="btn-ghost" style={{ padding: '7px 11px', fontSize: 13 }} onClick={actions.logout}>Ut</button>
            </div>
            <div className="tabs">
              {TABS.map(t => (
                <button key={t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>{t.label}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="admin-top">
            <div style={{ fontWeight: 700, fontSize: 16 }}>{title}</div>
            <div className="spacer" />
            {year != null && <div className="muted" style={{ fontSize: 13.5, fontWeight: 600 }}>Säsong {year}</div>}
          </div>
        )}

        <div className="admin-page">
          {tab === 'overview' && (season
            ? <Overview data={data} year={year} season={season} dteams={dteams} setYear={setYear} openDetail={setDetailId} openOffice={() => setTab('office')} />
            : <NoSeason goSettings={() => setTab('settings')} />)}
          {tab === 'teams' && (season
            ? <Teams year={year} season={season} dteams={dteams} actions={actions} />
            : <NoSeason goSettings={() => setTab('settings')} />)}
          {tab === 'office' && (season
            ? <Office year={year} season={season} actions={actions} />
            : <NoSeason goSettings={() => setTab('settings')} />)}
          {tab === 'settings' && <Settings data={data} year={year} season={season} setYear={setYear} actions={actions} />}
        </div>
      </main>

      {detail && <TeamDetail year={year} team={detail} season={season} types={teamTypes(season)} actions={actions} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function NoSeason({ goSettings }) {
  return (
    <div className="card card-pad" style={{ textAlign: 'center' }}>
      <h2 className="title">Ingen säsong ännu</h2>
      <p className="sub">Börja med att skapa en säsong under Inställningar. Lottyperna fylls i med standardvärden som du kan ändra.</p>
      <button className="btn" onClick={goSettings}>Till Inställningar</button>
    </div>
  );
}

/* ================= ÖVERSIKT ================= */

function Overview({ data, year, season, dteams, setYear, openDetail, openOffice }) {
  const [sort, setSort] = useState({ key: 'order', dir: 'asc' });   // 'order' = the drag-and-drop order
  const totals = seasonTotals(dteams, season);
  const perYear = yearStats(data);
  const types = teamTypes(season);
  const sorted = sort.key === 'order' ? dteams : sortTeams(dteams, sort.key, sort.dir);

  const onSort = key => setSort(s => (s.key === key
    ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  const arrow = key => (sort.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');
  const Th = ({ k, className = '', children, style }) => (
    <th className={'sortable ' + className} style={style} onClick={() => onSort(k)}>{children}{arrow(k)}</th>
  );

  return (
    <>
      <h1>Översikt</h1>
      <p className="lead">Hur alla lag ligger till säsongen {year}.</p>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(158px,1fr))', marginBottom: 24 }}>
        <Kpi label="Total försäljning" value={kr(totals.sales)} />
        <Kpi label="Intjänat till SIF" value={kr(totals.club)}
          note={totals.office.club ? `varav kansliet ${kr(totals.office.club)}` : null} mutedNote />
        <Kpi label="Lagkassor" value={kr(totals.kassa)} soft />
        <Kpi label="Lag klara" value={`${totals.done} / ${totals.teamCount}`} />
        <Kpi label="Sålda lotter" value={totals.lots} />
        <Kpi label="Sålda kalendrar" value={totals.cal} />
        <div className="kpi" style={{ gridColumn: 'span 2' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <div className="kpi-label amb">Returer per typ</div>
            <div className="mono muted" style={{ fontSize: 12 }}>{totals.ret} totalt</div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 22px', marginTop: 9 }}>
            {totals.retCells.map(rc => (
              <div key={rc.id}>
                <div className="muted" style={{ fontSize: 11, fontWeight: 600 }}>{rc.name}</div>
                <div className="mono amb" style={{ fontSize: 19, fontWeight: 600, marginTop: 2 }}>{rc.ret}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden', marginBottom: 18 }}>
        <div className="card-head">
          <h2>Försäljning per säsong</h2>
          <span>Klicka på en säsong för att öppna den</span>
        </div>
        <div className="table-wrap">
          <table className="t" style={{ minWidth: 740 }}>
            <thead>
              <tr>
                <th className="l" style={{ padding: '12px 18px' }}>Säsong</th>
                <th className="r" style={{ padding: '12px 10px' }}>Total försäljning</th>
                <th className="r" style={{ padding: '12px 10px' }}>Intjänat till SIF</th>
                <th className="r green" style={{ padding: '12px 10px' }}>Lagkassor</th>
                <th>Lotter</th>
                <th>Kalendrar</th>
                {types.map(t => <th key={t.id} className="amb">Retur {t.name}</th>)}
                <th style={{ padding: '12px 16px' }}>Lag klara</th>
              </tr>
            </thead>
            <tbody>
              {perYear.map(ys => (
                <tr key={ys.year} className={'click' + (ys.year === year ? ' current' : '')} onClick={() => setYear(ys.year)}>
                  <td style={{ padding: '11px 18px', whiteSpace: 'nowrap' }}>
                    <span className="mono" style={{ fontWeight: 700, fontSize: 15 }}>{ys.year}</span>
                    {ys.year === year && <span className="pending">Vald</span>}
                  </td>
                  <td className="num-r" style={{ padding: 10 }}>{kr(ys.sales)}</td>
                  <td className="num-r" style={{ padding: 10 }}>{kr(ys.club)}</td>
                  <td className="num-r green" style={{ padding: 10, fontWeight: 600 }}>{kr(ys.kassa)}</td>
                  <td className="num-c">{ys.lots}</td>
                  <td className="num-c">{ys.cal}</td>
                  {types.map(t => <td key={t.id} className="num-c amb">{ys.retById[t.id] || 0}</td>)}
                  <td className="num-c" style={{ padding: '10px 16px' }}>{ys.done} / {ys.teamCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-head">
          <h2>Alla lag</h2>
          {sort.key === 'order'
            ? <span>Klicka på ett lag för detaljer</span>
            : <button className="btn-ghost" style={{ fontSize: 12.5, padding: '5px 10px' }}
                onClick={() => setSort({ key: 'order', dir: 'asc' })}>Visa i egen ordning</button>}
        </div>
        <div className="table-wrap">
          <table className="t" style={{ minWidth: 780 }}>
            <thead>
              <tr>
                <Th k="name" className="l" style={{ padding: '12px 18px' }}>Lag</Th>
                <Th k="players">Spelare (ledare)</Th>
                <Th k="lots">Lotter</Th>
                <Th k="cal">Kalendrar</Th>
                {types.map(t => <Th key={t.id} k={'ret:' + t.id} className="amb">Retur {t.name}</Th>)}
                <Th k="club" className="r" style={{ padding: '12px 10px' }}>Intjänat</Th>
                <Th k="kassa" className="r green" style={{ padding: '12px 10px' }}>Lagkassa</Th>
                <Th k="status" className="l" style={{ padding: '12px 16px' }}>Status</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(t => (
                <tr key={t.id} className="click" onClick={() => openDetail(t.id)}>
                  <td style={{ padding: '11px 18px', fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap' }}>{t.name}</td>
                  <td className="num-c muted nowrap">{t.players.length} ({t.leaders.length})</td>
                  <td className="num-c">{t.lots}</td>
                  <td className="num-c">{t.cal}</td>
                  {t.byType.map(b => <td key={b.id} className="num-c amb">{b.ret}</td>)}
                  <td className="num-r" style={{ padding: '9px 10px' }}>{kr(t.club)}</td>
                  <td className="num-r green" style={{ padding: '9px 10px', fontWeight: 600 }}>{kr(t.kassa)}</td>
                  <td style={{ padding: '9px 16px' }}>
                    {t.done ? <span className="badge-done">✓ Klar</span> : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
                        <Bar pct={t.prog} />
                        <span className="mono muted" style={{ fontSize: 11.5 }}>{t.prog}%</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!sorted.length && <tr><td colSpan={types.length + 7} className="empty">Inga lag den här säsongen. Skapa dem under Lag &amp; spelare.</td></tr>}
              {/* Kansliet alltid sist, utanför sorteringen — inga returer, ingen lagkassa. */}
              <tr className="click office-row" onClick={openOffice}>
                <td style={{ padding: '11px 18px', fontWeight: 700, fontSize: 14.5, whiteSpace: 'nowrap' }}>Kansliet</td>
                <td className="num-c muted">–</td>
                <td className="num-c">{totals.office.lots}</td>
                <td className="num-c">{totals.office.cal}</td>
                {types.map(t => <td key={t.id} className="num-c muted">–</td>)}
                <td className="num-r" style={{ padding: '9px 10px' }}>{kr(totals.office.club)}</td>
                <td className="num-r muted" style={{ padding: '9px 10px' }}>–</td>
                <td style={{ padding: '9px 16px' }}><span className="muted" style={{ fontSize: 12.5 }}>Allt till föreningen</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ================= KANSLIET ================= */

/**
 * Lotter som kansliet säljer själv. Ingen tilldelning och inga returer, och
 * hela intjäningen går till föreningen — inget hamnar i någon lagkassa.
 */
const today = () => new Date().toLocaleDateString('sv-SE');   // YYYY-MM-DD, local time
const dayLabel = d => new Date(d + 'T12:00').toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const timeLabel = at => new Date(at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
const countLabel = (lots, cal) => [
  lots && `${lots} ${lots === 1 ? 'lott' : 'lotter'}`,
  cal && `${cal} ${cal === 1 ? 'kalender' : 'kalendrar'}`,
].filter(Boolean).join(' · ') || '–';

function Office({ year, season, actions }) {
  const office = officeStats(season);
  const { days, legacy } = officeDays(season);
  const [to, setTo] = useState('');            // '' = kansliet sells, otherwise a team id
  const byTeam = handoutsByTeam(season);
  const allTypes = season.lotteryTypes;
  const types = to ? teamTypes(season) : allTypes;   // kansliet-only types can't go to a team
  const teamName = id => season.teams.find(t => t.id === id)?.name || 'Borttaget lag';
  const [date, setDate] = useState(today);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (to && !season.teams.some(t => t.id === to)) setTo(''); }, [season.teams, to]);

  const count = id => Math.max(0, parseInt(qty[id] || '0', 10) || 0);
  const items = Object.fromEntries(types.map(t => [t.id, count(t.id)]).filter(([, n]) => n > 0));
  const sum = types.reduce((a, t) => a + count(t.id) * (t.price || 0), 0);
  const setOffice = office => actions.patchSeason(year, s => ({ ...s, office, handouts: office.handouts || {} }));

  const handOut = async e => {
    e.preventDefault();
    if (!Object.keys(items).length || busy) return;
    setBusy(true);
    const what = types.filter(t => items[t.id]).map(t => `${items[t.id]} ${t.name}`).join(', ');
    const saved = await actions.run(() => api.post(`/seasons/${year}/office/sales`, { date, items, teamId: to || undefined }),
      { ok: to ? `Utlämnat till ${teamName(to)}: ${what}.` : `Registrerat: ${what}.` });
    setBusy(false);
    if (saved) { setOffice(saved); setQty({}); }
  };

  const removeSale = async sale => {
    const ok = await actions.confirm({
      title: sale.teamId ? 'Ta bort utlämningen?' : 'Ta bort försäljningen?',
      message: `${sale.label}${sale.teamId ? ` till ${teamName(sale.teamId)}` : ''} (${dayLabel(sale.date)} kl ${timeLabel(sale.at)}) tas bort från statistiken.`,
      okLabel: 'Ta bort', danger: true,
    });
    if (!ok) return;
    const saved = await actions.run(() => api.del(`/seasons/${year}/office/sales/${sale.id}`));
    if (saved) setOffice(saved);
  };

  return (
    <>
      <h1>Kansliet</h1>
      <p className="lead">Lotter som kansliet säljer säsongen {year} – hela intjäningen går till föreningen. Kansliet kan också lämna ut extra lotter till ett lag.</p>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', marginBottom: 18 }}>
        <Kpi label="Total försäljning" value={kr(office.sales)} />
        <Kpi label="Intjänat till SIF" value={kr(office.club)} />
        <Kpi label="Sålda lotter" value={office.lots} />
        <Kpi label="Sålda kalendrar" value={office.cal} />
      </div>

      <form className="card card-pad" style={{ marginBottom: 18 }} onSubmit={handOut}>
        <h2 className="title" style={{ marginBottom: 14 }}>Lämna ut lotter</h2>
        {allTypes.length ? (
          <div className="handout">
            <div className="field">
              <label className="label" htmlFor="office-to">Till</label>
              <select id="office-to" className="input" value={to} onChange={e => setTo(e.target.value)}>
                <option value="">Kansliet (egen försäljning)</option>
                {season.teams.length > 0 && (
                  <optgroup label="Extra lotter till lag">
                    {season.teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="field">
              <label className="label" htmlFor="office-date">Datum</label>
              <input id="office-date" className="input" type="date" value={date} max={today()} required
                onChange={e => setDate(e.target.value || today())} />
            </div>
            {types.map(t => (
              <div key={t.id} className="field">
                <label className="label" htmlFor={'office-' + t.id}>{t.name} <span style={{ fontWeight: 500 }}>· {kr(t.price)}</span></label>
                <input id={'office-' + t.id} className="input mono" type="number" inputMode="numeric" min={0} placeholder="0"
                  value={qty[t.id] ?? ''} onFocus={e => e.target.select()}
                  onChange={e => setQty(q => ({ ...q, [t.id]: e.target.value }))} />
              </div>
            ))}
            <div className="field handout-sum">
              <div className="label">{to ? 'Värde' : 'Att betala'}</div>
              <div className="mono" style={{ fontSize: 20, fontWeight: 700, lineHeight: '42px' }}>{kr(sum)}</div>
            </div>
            <div className="field">
              <button className="btn" type="submit" disabled={busy || !Object.keys(items).length} style={{ whiteSpace: 'nowrap' }}>Lämna ut lotter</button>
            </div>
          </div>
        ) : <div className="empty">Inga lottyper den här säsongen. Lägg till dem under Inställningar.</div>}
      </form>

      <div className="card" style={{ overflow: 'hidden', marginBottom: 18 }}>
        <div className="card-head">
          <h2>Extra lotter till lag</h2>
          <span>Utlämnat av kansliet säsongen {year}</span>
        </div>
        <div className="table-wrap">
          <table className="t" style={{ minWidth: 480 }}>
            <thead>
              <tr>
                <th className="l" style={{ padding: '12px 18px' }}>Lag</th>
                {teamTypes(season).map(t => <th key={t.id}>{t.name}</th>)}
                <th className="r" style={{ padding: '12px 18px' }}>Senast</th>
              </tr>
            </thead>
            <tbody>
              {byTeam.map(r => (
                <tr key={r.team.id}>
                  <td className="name" style={{ padding: '10px 18px' }}>{r.team.name}</td>
                  {teamTypes(season).map(t => <td key={t.id} className="num-c">{r.items[t.id] || <span className="muted">–</span>}</td>)}
                  <td className="num-r muted" style={{ padding: '10px 18px' }}>{dayLabel(r.list[0].date)}</td>
                </tr>
              ))}
              {!byTeam.length && <tr><td colSpan={teamTypes(season).length + 2} className="empty">Inga extra lotter utlämnade till lag ännu.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-head">
          <h2>Utlämnat per dag</h2>
          <span>{office.sales ? `Totalt ${kr(office.sales)}` : ''}</span>
        </div>
        <div className="table-wrap">
          <table className="t" style={{ minWidth: 620 }}>
            <thead>
              <tr>
                <th className="l" style={{ padding: '12px 18px' }}>Datum</th>
                <th className="l">Utlämnat</th>
                <th className="r" title="Kansliets egen försäljning">Försäljning</th>
                <th className="r">Intjänat till SIF</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {days.map(d => (
                <Fragment key={d.date}>
                  <tr className="day-row">
                    <td style={{ padding: '11px 18px' }}>{dayLabel(d.date)}</td>
                    <td>{countLabel(d.lots, d.cal)}</td>
                    <td className="num-r">{kr(d.total)}</td>
                    <td className="num-r green">{kr(d.club)}</td>
                    <td />
                  </tr>
                  {d.sales.map(x => (
                    <tr key={x.id}>
                      <td className="mono muted" style={{ padding: '8px 18px 8px 34px', fontSize: 12.5 }}>kl {timeLabel(x.at)}</td>
                      <td>
                        {x.label}
                        {x.teamId && <span className="to-team">Till {teamName(x.teamId)}</span>}
                      </td>
                      <td className="num-r muted">{x.teamId ? '–' : kr(x.sales)}</td>
                      <td className="num-r muted">{x.teamId ? '–' : kr(x.club)}</td>
                      <td style={{ textAlign: 'right', padding: '6px 14px' }}>
                        <button className="btn-danger" onClick={() => removeSale(x)}>Ta bort</button>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {legacy && (
                <tr className="day-row">
                  <td style={{ padding: '11px 18px' }}>Utan datum</td>
                  <td>{legacy.label}</td>
                  <td className="num-r">{kr(legacy.sales)}</td>
                  <td className="num-r green">{kr(legacy.club)}</td>
                  <td />
                </tr>
              )}
              {!days.length && !legacy && <tr><td colSpan={5} className="empty">Inga lotter utlämnade ännu.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ================= LAG & SPELARE ================= */

/** A value remembered in this browser; falls back to `initial` in a private window. */
function useStored(key, initial) {
  const [value, setValue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) ?? initial; } catch { return initial; }
  });
  const set = next => setValue(v => {
    const n = typeof next === 'function' ? next(v) : next;
    try { localStorage.setItem(key, JSON.stringify(n)); } catch { /* private window — just don't remember */ }
    return n;
  });
  return [value, set];
}

/**
 * Which of a page's sections are open, remembered in this browser — every
 * section starts open. Returns [isOpen(id), toggle(id)].
 */
function useFolds(page) {
  const [closed, setClosed] = useStored('sif-folds:' + page, {});
  return [id => !closed[id], id => setClosed(c => ({ ...c, [id]: !c[id] }))];
}

/** The ▾ that minimises a section; `children` makes the title itself clickable too. */
function FoldButton({ open, onToggle, label, children }) {
  return (
    <button type="button" className="fold" aria-expanded={open} onClick={onToggle}
      title={open ? 'Minimera' : 'Visa'} aria-label={children ? undefined : (open ? 'Minimera ' : 'Visa ') + label}>
      <span className="fold-ico" aria-hidden="true">▾</span>{children}
    </button>
  );
}

const TEAM_VIEWS = [
  { id: 'order', label: 'Lag & ordning' },
  { id: 'players', label: 'Spelare' },
  { id: 'access', label: 'Lösenord & ledare' },
];

/** Lag & spelare, split into three views so nothing needs much scrolling. */
function Teams({ year, season, dteams, actions }) {
  const [view, setView] = useStored('sif-teams-view', 'order');
  const [manageId, setManageId] = useState(dteams[0]?.id || '');
  const missing = dteams.filter(t => !t.hasPassword).length;

  useEffect(() => {
    if (!dteams.some(t => t.id === manageId)) setManageId(dteams[0]?.id || '');
  }, [dteams, manageId]);

  const openPlayers = id => { setManageId(id); setView('players'); };
  const current = TEAM_VIEWS.some(v => v.id === view) ? view : 'order';

  return (
    <>
      <h1>Lag &amp; spelare</h1>
      <p className="lead">Lag, spelare, lösenord och ledare för säsongen {year}.</p>
      <div className="subtabs" role="tablist">
        {TEAM_VIEWS.map(v => (
          <button key={v.id} role="tab" aria-selected={current === v.id} className={current === v.id ? 'on' : ''} onClick={() => setView(v.id)}>
            {v.label}
            {v.id === 'players' && <span className="subtab-n">{dteams.reduce((n, t) => n + t.players.length, 0)}</span>}
            {v.id === 'access' && missing > 0 && <span className="subtab-n warn" title={`${missing} lag saknar lösenord`}>{missing}</span>}
          </button>
        ))}
      </div>

      {current === 'order' && <TeamOrder year={year} season={season} dteams={dteams} actions={actions} openPlayers={openPlayers} />}
      {current === 'players' && <Players year={year} dteams={dteams} actions={actions} manageId={manageId} setManageId={setManageId} />}
      {current === 'access' && (dteams.length
        ? <TeamAccess year={year} dteams={dteams} actions={actions} />
        : <div className="card card-pad empty">Skapa lag under <b>Lag &amp; ordning</b> först.</div>)}
    </>
  );
}

function TeamOrder({ year, season, dteams, actions, openPlayers }) {
  const [isOpen, toggle] = useFolds('teams');
  const fold = id => ({ open: isOpen(id), onToggle: () => toggle(id) });
  const [teamForm, setTeamForm] = useState({ name: '', password: '', leaders: '' });
  const [busy, setBusy] = useState(false);

  const addTeam = async () => {
    if (!teamForm.name.trim() || busy) return;
    setBusy(true);
    const team = await actions.run(() => api.post(`/seasons/${year}/teams`, teamForm), { ok: 'Laget är skapat.' });
    setBusy(false);
    if (team) {
      actions.setTeam(year, team);
      setTeamForm({ name: '', password: '', leaders: '' });
    }
  };

  /* Drag-and-drop: show the new order at once, put it back if the save fails. */
  const reorder = async ids => {
    const before = season.teams;
    actions.patchSeason(year, s => ({ ...s, teams: ids.map(id => s.teams.find(t => t.id === id)).filter(Boolean) }));
    const teams = await actions.run(() => api.put(`/seasons/${year}/team-order`, { ids }));
    actions.patchSeason(year, s => ({ ...s, teams: teams || before }));
  };

  return (
    <div className="order-layout">
      <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
        <h2 className="title" style={{ marginBottom: isOpen('create') ? 16 : 0 }}><FoldButton {...fold('create')}>Skapa nytt lag</FoldButton></h2>
        {isOpen('create') && (<>
          <div className="field">
            <label className="label">Lagnamn</label>
            <input className="input" value={teamForm.name} placeholder="t.ex. P2016"
              onChange={e => setTeamForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="field">
            <label className="label">Lösenord <span style={{ fontWeight: 500 }}>(valfritt)</span></label>
            <input className="input" value={teamForm.password} placeholder="tomt = generera under Lösenord & ledare"
              onChange={e => setTeamForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label className="label">Ledare <span style={{ fontWeight: 500 }}>(en per rad, e-post valfritt)</span></label>
            <textarea className="input" rows={3} value={teamForm.leaders} placeholder={'Anna Andersson anna@exempel.se\nBertil Berg'}
              onChange={e => setTeamForm(f => ({ ...f, leaders: e.target.value }))} />
          </div>
          <button className="btn btn-block" style={{ marginTop: 'auto', padding: 11, borderRadius: 10 }} onClick={addTeam} disabled={busy}>Skapa lag</button>
        </>)}
      </div>

      <div className={'card' + (isOpen('order') ? '' : ' folded')} style={{ overflow: 'hidden' }}>
        <div className="card-head">
          <h2><FoldButton {...fold('order')}>Lagens ordning</FoldButton></h2>
          <span>{isOpen('order') && dteams.length ? 'Dra i ⋮⋮ för att sortera' : `${dteams.length} lag`}</span>
        </div>
        {isOpen('order') && (dteams.length
          ? <SortableList items={dteams} onReorder={reorder}
              renderItem={t => (
                <>
                  <button type="button" className="row-pick" onClick={() => openPlayers(t.id)} title="Visa spelarna">{t.name}</button>
                  <span className="mono muted" style={{ fontSize: 12.5 }}>{t.players.length} spelare</span>
                </>
              )} />
          : <div className="empty">Inga lag ännu.</div>)}
      </div>
    </div>
  );
}

function Players({ year, dteams, actions, manageId, setManageId }) {
  const [playerName, setPlayerName] = useState('');
  const [busy, setBusy] = useState(false);
  const manage = dteams.find(t => t.id === manageId);
  const others = dteams.filter(t => t.id !== manageId);

  if (!manage) return <div className="card card-pad empty">Skapa lag under <b>Lag &amp; ordning</b> först.</div>;

  const addPlayer = async () => {
    if (!playerName.trim() || busy) return;
    setBusy(true);
    const team = await actions.run(() => api.post(`/seasons/${year}/teams/${manage.id}/players`, { name: playerName }));
    setBusy(false);
    if (team) { actions.setTeam(year, team); setPlayerName(''); }
  };

  /* Excel import: read names in the browser, confirm, then add them in one call. */
  const importPlayers = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || busy) return;
    let names;
    try {
      names = await readPlayerNames(file);
    } catch (err) {
      actions.notify(err.message || 'Kunde inte läsa filen.', true);
      return;
    }
    if (!names.length) { actions.notify('Hittade inga namn i filen.', true); return; }
    const ok = await actions.confirm({
      title: `Importera ${names.length} spelare?`,
      message: <>
        Spelarna läggs till i <b>{manage.name}</b> ({year}). Namn som redan finns i laget hoppas över.
        <div className="dialog-list">{names.map(n => <div key={n}>{n}</div>)}</div>
      </>,
      okLabel: 'Importera',
    });
    if (!ok) return;
    setBusy(true);
    const res = await actions.run(() => api.post(`/seasons/${year}/teams/${manage.id}/players/import`, { names }));
    setBusy(false);
    if (res) {
      actions.setTeam(year, res.team);
      actions.notify(`${res.added} spelare importerade` + (res.skipped ? ` (${res.skipped} fanns redan).` : '.'));
    }
  };

  const movePlayer = async (p, toId) => {
    const to = dteams.find(t => t.id === toId);
    if (!to) return;
    const ok = await actions.confirm({
      title: `Flytta ${p.name}?`,
      message: <>{p.name} flyttas från <b>{manage.name}</b> till <b>{to.name}</b> ({year}). Tilldelning och registrerad försäljning följer med.</>,
      okLabel: 'Flytta',
    });
    if (!ok) return;
    setBusy(true);
    const res = await actions.run(() => api.post(`/seasons/${year}/teams/${manage.id}/players/${p.id}/move`, { toTeamId: to.id }),
      { ok: `${p.name} spelar nu i ${to.name}.` });
    setBusy(false);
    if (res) { actions.setTeam(year, res.from); actions.setTeam(year, res.to); }
  };

  const removePlayer = async p => {
    const ok = await actions.confirm({
      title: `Ta bort ${p.name}?`,
      message: <>{p.name} tas bort från <b>{manage.name}</b> ({year}). Registrerad försäljning försvinner.</>,
      okLabel: 'Ta bort', danger: true,
    });
    if (!ok) return;
    const team = await actions.run(() => api.del(`/seasons/${year}/teams/${manage.id}/players/${p.id}`));
    if (team) actions.setTeam(year, team);
  };

  return (
    <div className="players-layout">
      <nav className="card team-pick" aria-label="Välj lag">
        {dteams.map(t => (
          <button key={t.id} type="button" className={t.id === manageId ? 'on' : ''} aria-current={t.id === manageId} onClick={() => setManageId(t.id)}>
            <span>{t.name}</span><span className="mono">{t.players.length}</span>
          </button>
        ))}
      </nav>

      <div className="card" style={{ overflow: 'hidden', minWidth: 0 }}>
        <div className="card-head">
          <TeamName year={year} team={manage} actions={actions} prefix="Spelare i " />
          <span>{manage.players.length} spelare · {manage.leaders.length} ledare</span>
        </div>
        <div className="add-player">
          <input className="input input-sm" value={playerName} placeholder="Ny spelare – för- och efternamn" aria-label="Spelarens namn"
            onChange={e => setPlayerName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addPlayer(); }} />
          <button className="btn" style={{ padding: '8px 14px', fontSize: 14, borderRadius: 9, whiteSpace: 'nowrap' }} onClick={addPlayer} disabled={busy || !playerName.trim()}>Lägg till</button>
          <label className="btn-ghost" style={{ whiteSpace: 'nowrap', ...(busy ? { opacity: .55, pointerEvents: 'none' } : {}) }}
            title="Excel-fil (.xlsx) med en kolumn som heter Namn">
            Importera från Excel
            <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden onChange={importPlayers} disabled={busy} />
          </label>
        </div>
        {manage.players.map(p => (
          <div key={p.id} className="list-row player-row">
            <div style={{ flex: 1, fontWeight: 600, fontSize: 14.5, minWidth: 140 }}>{p.name}</div>
            <div className="mono muted" style={{ fontSize: 12.5 }}>Mål: {p.targetLabel}</div>
            <div className="mono green" style={{ fontSize: 13.5, fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{kr(p.earned)}</div>
            {others.length > 0 && (
              <select className="input input-sm move-select" value="" disabled={busy} aria-label={`Flytta ${p.name} till annat lag`}
                onChange={e => movePlayer(p, e.target.value)}>
                <option value="">Flytta till…</option>
                {others.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            )}
            <button className="btn-danger" onClick={() => removePlayer(p)} disabled={busy}>Ta bort</button>
          </div>
        ))}
        {!manage.players.length && <div className="empty">Inga spelare ännu.</div>}
        <div className="hint" style={{ margin: 14 }}>
          Nya spelare får standardtilldelningen per lottyp. Justera under <b>Inställningar → Tilldelning per spelare</b>.
        </div>
      </div>
    </div>
  );
}

/* ================= LÖSENORD & LEDARE ================= */

/**
 * Every team's password and leaders in one table. Generating a password saves
 * it at once; passwords stay hidden until admin asks to see one ("Visa") or
 * all of them ("Visa alla"), and are forgotten again when admin leaves the
 * page. Leaders save as soon as one is added, changed or removed.
 */
function TeamAccess({ year, dteams, actions }) {
  const [plain, setPlain] = useState(null);     // teamId → password, fetched on first "Visa"
  const [shown, setShown] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(null);       // what is working: 'all' | 'missing' | 'show' | a team id
  const missing = dteams.filter(t => !t.hasPassword);
  const visible = t => showAll || shown.has(t.id);

  useEffect(() => { setPlain(null); setShown(new Set()); setShowAll(false); }, [year]);

  const loadPasswords = async () => {
    if (plain) return true;
    setBusy('show');
    const res = await actions.run(() => api.get(`/seasons/${year}/passwords`));
    setBusy(null);
    if (res) setPlain(res.passwords);
    return !!res;
  };

  const toggleOne = async t => {
    if (visible(t)) {
      if (showAll) { setShowAll(false); setShown(new Set(dteams.filter(x => x.id !== t.id).map(x => x.id))); }
      else setShown(s => { const n = new Set(s); n.delete(t.id); return n; });
      return;
    }
    if (await loadPasswords()) setShown(s => new Set(s).add(t.id));
  };

  const toggleAll = async () => {
    if (showAll) { setShowAll(false); setShown(new Set()); return; }
    if (await loadPasswords()) setShowAll(true);
  };

  /* Saved straight away — ask first when it replaces a password a team already uses. */
  const generate = async (teams, what) => {
    const replacing = teams.filter(t => t.hasPassword);
    if (replacing.length) {
      const ok = await actions.confirm(what === 'all' ? {
        title: 'Nya lösenord till alla lag?',
        message: <>{replacing.length} av {teams.length} lag har redan ett lösenord. De gamla slutar fungera direkt, så alla lag behöver få sitt nya.</>,
        okLabel: 'Generera och spara',
      } : {
        title: `Nytt lösenord till ${teams[0].name}?`,
        message: <>Det gamla lösenordet slutar fungera direkt, så ledarna behöver få det nya.</>,
        okLabel: 'Generera och spara',
      });
      if (!ok) return;
    }
    setBusy(what);
    const res = await actions.run(() => api.post(`/seasons/${year}/passwords/generate`, { teamIds: teams.map(t => t.id) }),
      { ok: teams.length === 1 ? `Nytt lösenord sparat för ${teams[0].name}.` : `${teams.length} nya lösenord är sparade.` });
    setBusy(null);
    if (!res) return;
    res.teams.forEach(t => actions.setTeam(year, t));
    setPlain(p => (p ? { ...p, ...res.passwords } : p));
    actions.load().catch(() => {});   // later seasons got the new password too
  };

  const copyList = async () => {
    try {
      await navigator.clipboard.writeText(dteams.map(t => `${t.name}\t${plain?.[t.id] || ''}`).join('\n'));
      actions.notify('Listan är kopierad.');
    } catch {
      actions.notify('Kunde inte kopiera – skriv av listan i stället.', true);
    }
  };

  const saveLeaders = async (team, leaders) => {
    const res = await actions.run(() => api.patch(`/seasons/${year}/teams/${team.id}`, { leaders }));
    if (res) actions.setTeam(year, res.team);
  };

  const small = { fontSize: 12.5, padding: '5px 10px' };
  const working = label => <><span className="spin-sm" aria-hidden="true" />{label}</>;
  const generating = busy && busy !== 'show';
  const saving = t => busy === t.id || busy === 'all' || (busy === 'missing' && !t.hasPassword);

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="card-head" style={{ flexWrap: 'wrap' }}>
        <h2>Lösenord &amp; ledare</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {showAll && <button className="btn-ghost" onClick={copyList}>Kopiera lista</button>}
          <button className="btn-ghost" onClick={toggleAll} disabled={!!busy || missing.length === dteams.length} aria-pressed={showAll}>
            {busy === 'show' ? working('Hämtar…') : showAll ? 'Dölj alla lösenord' : 'Visa alla lösenord'}
          </button>
          {missing.length > 0 && missing.length < dteams.length && (
            <button className="btn-ghost" onClick={() => generate(missing, 'missing')} disabled={!!busy}>
              {busy === 'missing' ? working('Genererar…') : `Generera för lag utan lösenord (${missing.length})`}
            </button>
          )}
          <button className="btn" style={{ padding: '8px 14px', fontSize: 14, borderRadius: 9 }} onClick={() => generate(dteams, 'all')} disabled={!!busy}>
            {busy === 'all' ? working(`Genererar ${dteams.length} lösenord…`) : 'Generera lösenord till alla lag'}
          </button>
        </div>
      </div>
      <div className="hint" style={{ margin: '14px 18px 4px' }}>
        Ledare: skriv namn och e-post (valfritt), t.ex. <b>Anna Andersson anna@exempel.se</b>, och tryck Enter. Klicka på en ledare för att ändra e-posten.
      </div>
      <div className={'table-wrap' + (generating ? ' is-busy' : '')} aria-busy={!!generating}>
        <table className="t" style={{ minWidth: 700 }}>
          <thead>
            <tr>
              <th className="l" style={{ padding: '12px 18px' }}>Lag</th>
              <th className="l">Lösenord</th>
              <th className="l" style={{ padding: '12px 18px 12px 10px' }}>Ledare</th>
            </tr>
          </thead>
          <tbody>
            {dteams.map(t => {
              const pw = plain?.[t.id];
              return (
                <tr key={t.id}>
                  <td className="name" style={{ padding: '10px 18px', whiteSpace: 'nowrap' }}>{t.name}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <div className="pw-cell">
                      {saving(t)
                        ?<span className="pw-missing muted">{working('Sparar…')}</span>
                        : !t.hasPassword
                          ? <span className="amb pw-missing">Saknar lösenord</span>
                          : visible(t)
                            ? (pw
                              ? <span className="pw-shown mono">{pw}</span>
                              : <span className="pw-missing muted" title="Sparat innan lösenord gick att visa. Byt det för att kunna se det.">Okänt</span>)
                            : <input className="input input-sm mono" type="password" value="••••••" readOnly tabIndex={-1} aria-label="Dolt lösenord" />}
                      {t.hasPassword && (
                        <button className="btn-ghost" style={small} onClick={() => toggleOne(t)} disabled={!!busy} aria-pressed={visible(t)}>
                          {visible(t) ? 'Dölj' : 'Visa lösenord'}
                        </button>
                      )}
                      <button className="btn-ghost" style={small} onClick={() => generate([t], t.id)} disabled={!!busy}>
                        {t.hasPassword ? 'Byt' : 'Generera'}
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '8px 18px 8px 10px' }}>
                    <LeaderEditor team={t} actions={actions} onSave={leaders => saveLeaders(t, leaders)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LEADER_SPLIT = /[\n,;]+/;
const EMAIL_RE = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
const EMAIL_IN = /<?([^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+)>?/;

/** "Anna Andersson anna@exempel.se" → { name, email }; the e-mail is optional. */
function parseLeader(raw) {
  const m = EMAIL_IN.exec(raw);
  const email = m ? m[1].toLowerCase() : '';
  const name = (m ? raw.replace(m[0], ' ') : raw).trim().replace(/\s+/g, ' ') || email;
  if (!name) return null;
  return email ? { name, email } : { name };
}

/**
 * Leaders as chips: type a name (and an e-mail if you have it) and press
 * Enter or comma to add, × to remove, click a chip to change its e-mail.
 * Pasting a list — one per line or comma separated — adds them all.
 */
function LeaderEditor({ team, actions, onSave }) {
  const [draft, setDraft] = useState('');
  const leaders = team.leaders;

  const add = raw => {
    const seen = new Set(leaders.map(l => l.name.toLowerCase()));
    const fresh = [];
    for (const l of raw.split(LEADER_SPLIT).map(parseLeader)) {
      if (!l || seen.has(l.name.toLowerCase())) continue;
      seen.add(l.name.toLowerCase());
      fresh.push(l);
    }
    setDraft('');
    if (fresh.length) onSave([...leaders, ...fresh]);
  };

  const editEmail = async leader => {
    const email = await actions.prompt({
      title: leader.email ? 'Ändra e-post' : 'Lägg till e-post',
      message: <>E-post till <b>{leader.name}</b>, ledare för {team.name}. Lämna tomt för att ta bort den.</>,
      input: {
        label: 'E-post', type: 'email', value: leader.email || '', placeholder: 'namn@exempel.se',
        validate: v => (v.trim() && !EMAIL_RE.test(v.trim()) ? 'Det ser inte ut som en e-postadress.' : ''),
      },
      okLabel: 'Spara',
    });
    if (email === null || email === undefined) return;
    const next = email.trim().toLowerCase();
    if (next === (leader.email || '')) return;
    onSave(leaders.map(l => (l === leader ? (next ? { name: l.name, email: next } : { name: l.name }) : l)));
  };

  return (
    <div className="leaders">
      {leaders.map(l => (
        <span key={l.name} className="leader-chip">
          <button type="button" className="leader-name" onClick={() => editEmail(l)}
            title={l.email ? `${l.email} – klicka för att ändra` : 'Lägg till e-post'}>
            {l.name}
            {l.email ? <span className="leader-mail">{l.email}</span> : <span className="leader-mail add">+ e-post</span>}
          </button>
          <button type="button" className="leader-x" aria-label={`Ta bort ${l.name}`} title="Ta bort"
            onClick={() => onSave(leaders.filter(x => x !== l))}>×</button>
        </span>
      ))}
      <input className="leader-input" value={draft} placeholder={leaders.length ? '+ ledare' : 'Namn e-post, Enter'}
        aria-label={`Lägg till ledare i ${team.name}`}
        onChange={e => (/[,;]/.test(e.target.value) ? add(e.target.value) : setDraft(e.target.value))}
        onPaste={e => {
          const text = e.clipboardData.getData('text');
          if (LEADER_SPLIT.test(text)) { e.preventDefault(); add(draft + text); }
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); add(draft); }
          if (e.key === 'Backspace' && !draft && leaders.length) onSave(leaders.slice(0, -1));
        }}
        onBlur={() => { if (draft.trim()) add(draft); }} />
    </div>
  );
}

/* ================= INSTÄLLNINGAR ================= */

function Settings({ data, year, season, setYear, actions }) {
  const [newYear, setNewYear] = useState(String((data.years.length ? Math.max(...data.years) : new Date().getFullYear() - 1) + 1));
  const [lt, setLt] = useState({ name: '', price: '', profit: '', kind: 'lott', officeOnly: false });
  const [alloc, setAlloc] = useState({});
  useEffect(() => { setAlloc(season ? { ...season.allocDefaults } : {}); }, [season?.allocDefaults, year]);

  const addYear = async () => {
    const y = parseInt(newYear, 10);
    if (!y || data.years.includes(y)) { actions.notify(`Säsongen ${newYear} finns redan.`, true); return; }
    const from = year != null ? year : undefined;
    const created = await actions.run(() => api.post('/seasons', { year: y, fromYear: from }),
      { ok: from != null ? `Säsongen ${y} är skapad med lagen från ${from}.` : `Säsongen ${y} är skapad.` });
    if (created) {
      await actions.load();
      setYear(y);
      setNewYear(String(y + 1));
    }
  };

  const updateType = async (id, field, value) => {
    const s = await actions.run(() => api.patch(`/seasons/${year}/types/${id}`, { [field]: value }));
    if (s) actions.patchSeason(year, cur => ({ ...cur, ...s }));
  };

  const removeType = async t => {
    const ok = await actions.confirm({
      title: `Ta bort ${t.name}?`,
      message: `Lottypen tas bort från säsongen ${year}.`,
      okLabel: 'Ta bort', danger: true,
    });
    if (!ok) return;
    const s = await actions.run(() => api.del(`/seasons/${year}/types/${t.id}`));
    if (s) actions.patchSeason(year, cur => ({ ...cur, ...s }));
  };

  const addType = async () => {
    if (!lt.name.trim()) return;
    const s = await actions.run(() => api.post(`/seasons/${year}/types`, lt));
    if (s) {
      actions.patchSeason(year, cur => ({ ...cur, ...s }));
      setLt({ name: '', price: '', profit: '', kind: 'lott', officeOnly: false });
    }
  };

  /* Saving the default only affects players added from now on. */
  const saveAlloc = async next => {
    setAlloc(next);
    const res = await actions.run(() => api.put(`/seasons/${year}/alloc`, { alloc: next, apply: false }));
    if (res) actions.patchSeason(year, cur => ({ ...cur, ...res.season }));
  };

  const applyAlloc = async () => {
    const ok = await actions.confirm({
      title: 'Tillämpa tilldelningen?',
      message: `Tilldelningen sätts på alla spelare i alla lag för ${year}. Tidigare justeringar per spelare skrivs över.`,
      okLabel: 'Tillämpa',
    });
    if (!ok) return;
    const res = await actions.run(() => api.put(`/seasons/${year}/alloc`, { alloc, apply: true }),
      { ok: 'Tilldelningen är uppdaterad för alla spelare.' });
    if (res) actions.patchSeason(year, cur => ({ ...cur, ...res.season, teams: res.teams || cur.teams }));
  };

  return (
    <>
      <h1>Inställningar</h1>
      <p className="lead" style={{ marginBottom: 22 }}>Säsonger, lottyper, priser och försäljningsmål.</p>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <h2 className="title">Säsonger</h2>
        <p className="sub">
          Allt registreras per år så att ni kan följa statistiken säsong för säsong.
          {year != null && <> En ny säsong kopierar lag, spelare och lottyper från <b>{year}</b> – försäljningen börjar om från noll.</>}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, alignItems: 'center' }}>
          {data.years.map(y => (
            <button key={y} className={'chip' + (y === year ? ' on' : '')} onClick={() => setYear(y)}>{y}</button>
          ))}
          {data.years.length > 0 && <div style={{ width: 1, height: 26, background: 'var(--bd)', margin: '0 4px' }} />}
          <input type="number" className="input input-sm mono" style={{ width: 96 }} value={newYear} onChange={e => setNewYear(e.target.value)} />
          <button className="btn-ghost" onClick={addYear}>+ Lägg till år</button>
        </div>
      </div>

      {season && (
        <>
          <div className="card card-pad" style={{ marginBottom: 16 }}>
            <h2 className="title" style={{ marginBottom: 4 }}>Lottyper &amp; priser</h2>
            <p className="sub" style={{ marginBottom: 14 }}>Välj vilka lottyper som säljs säsongen {year}. Ta bort de ni inte säljer i år – ändringen gäller bara detta år.</p>
            <div className="table-wrap">
              <table className="t" style={{ minWidth: 440 }}>
                <thead>
                  <tr>
                    <th className="l" style={{ padding: '11px 14px' }}>Lottyp</th>
                    <th className="l" style={{ padding: '11px 14px' }}>Pris (kr)</th>
                    <th className="l" style={{ padding: '11px 14px' }}>Intjäning / lott (kr)</th>
                    <th className="l" style={{ padding: '11px 14px' }}>Räknas som</th>
                    <th className="l" style={{ padding: '11px 14px' }}>Säljs av</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {season.lotteryTypes.map(t => (
                    <tr key={t.id}>
                      <td className="name" style={{ padding: '9px 14px' }}>{t.name}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <NumberInput className="input input-sm mono" style={{ width: 96 }} value={t.price} onCommit={v => updateType(t.id, 'price', v)} />
                      </td>
                      <td style={{ padding: '9px 14px' }}>
                        <NumberInput className="input input-sm mono" style={{ width: 96 }} value={t.profit} onCommit={v => updateType(t.id, 'profit', v)} />
                      </td>
                      <td className="muted" style={{ padding: '9px 14px' }}>{t.kind === 'kalender' ? 'Kalender' : 'Lott'}</td>
                      <td style={{ padding: '9px 14px' }}>
                        <select className="input input-sm" style={{ width: 'auto', padding: '7px 10px' }} value={t.officeOnly ? 'office' : 'all'}
                          onChange={e => updateType(t.id, 'officeOnly', e.target.value === 'office')}>
                          <option value="all">Lag &amp; kansliet</option>
                          <option value="office">Endast kansliet</option>
                        </select>
                      </td>
                      <td style={{ padding: '9px 14px', textAlign: 'right' }}>
                        <button className="btn-danger" onClick={() => removeType(t)}>Ta bort</button>
                      </td>
                    </tr>
                  ))}
                  {!season.lotteryTypes.length && <tr><td colSpan={6} className="empty">Inga lottyper den här säsongen.</td></tr>}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, alignItems: 'flex-end', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--bd)' }}>
              <div>
                <label className="label-sm">Ny lottyp</label>
                <input className="input input-sm" style={{ width: 150 }} placeholder="Namn" value={lt.name} onChange={e => setLt(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label className="label-sm">Pris</label>
                <input type="number" className="input input-sm mono" style={{ width: 84 }} placeholder="0" value={lt.price} onChange={e => setLt(f => ({ ...f, price: e.target.value }))} />
              </div>
              <div>
                <label className="label-sm">Intjäning</label>
                <input type="number" className="input input-sm mono" style={{ width: 84 }} placeholder="0" value={lt.profit} onChange={e => setLt(f => ({ ...f, profit: e.target.value }))} />
              </div>
              <div>
                <label className="label-sm">Räknas som</label>
                <select className="input input-sm" style={{ width: 'auto', padding: '9px 10px' }} value={lt.kind} onChange={e => setLt(f => ({ ...f, kind: e.target.value }))}>
                  <option value="lott">Lott</option>
                  <option value="kalender">Kalender</option>
                </select>
              </div>
              <div>
                <label className="label-sm">Säljs av</label>
                <select className="input input-sm" style={{ width: 'auto', padding: '9px 10px' }} value={lt.officeOnly ? 'office' : 'all'}
                  onChange={e => setLt(f => ({ ...f, officeOnly: e.target.value === 'office' }))}>
                  <option value="all">Lag &amp; kansliet</option>
                  <option value="office">Endast kansliet</option>
                </select>
              </div>
              <button className="btn-ghost" style={{ padding: '9px 14px', borderRadius: 9 }} onClick={addType}>+ Lägg till</button>
            </div>
          </div>

          <div className="card card-pad">
            <h2 className="title">Tilldelning per spelare</h2>
            <p className="sub" style={{ marginBottom: 16 }}>
              Antal av varje lottyp som varje spelare ska sälja säsongen {year}. Returer räknas ut automatiskt (tilldelning − sålda).
              Säljs mer än tilldelningen går vinsten till lagkassan. Nya spelare får den här tilldelningen.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
              {teamTypes(season).map(t => (
                <div key={t.id}>
                  <label className="label">{t.name} / spelare</label>
                  <NumberInput className="input mono" style={{ width: 118 }} value={alloc[t.id] || 0}
                    onCommit={v => saveAlloc({ ...alloc, [t.id]: v })} />
                </div>
              ))}
              <button className="btn" style={{ padding: '11px 16px', borderRadius: 10, fontSize: 14.5 }} onClick={applyAlloc} disabled={!teamTypes(season).length}>
                Tillämpa på alla spelare ({year})
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ================= LAGNAMN ================= */

/**
 * A team heading with a pencil that turns it into an input. A new name applies
 * to this season and every later one the team is in, so a typo copied into
 * next year is fixed too; earlier seasons keep the name they had.
 */
function TeamName({ year, team, actions, prefix = '', style }) {
  const [draft, setDraft] = useState(null);   // null = not editing
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDraft(null); }, [team.id]);

  const save = async () => {
    const name = (draft || '').trim();
    if (!name || name === team.name) { setDraft(null); return; }
    setBusy(true);
    const res = await actions.run(() => api.patch(`/seasons/${year}/teams/${team.id}`, { name }));
    if (res) {
      await actions.load();
      const later = res.years.filter(y => y !== year);
      actions.notify(later.length
        ? `Laget heter nu ${name} (${[year, ...later].join(', ')}).`
        : `Laget heter nu ${name}.`);
      setDraft(null);
    }
    setBusy(false);
  };

  if (draft === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <h2 style={style}>{prefix}{team.name}</h2>
        <button className="icon-btn" title="Byt namn på laget" aria-label="Byt namn på laget"
          onClick={() => setDraft(team.name)}>✎</button>
      </div>
    );
  }

  return (
    <form style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
      onSubmit={e => { e.preventDefault(); save(); }}>
      <input className="input input-sm" style={{ width: 200, fontWeight: 700 }} autoFocus
        value={draft} onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setDraft(null); } }} />
      <button type="submit" className="btn" style={{ padding: '8px 14px', fontSize: 14, borderRadius: 9 }} disabled={busy}>Spara</button>
      <button type="button" className="btn-ghost" onClick={() => setDraft(null)} disabled={busy}>Avbryt</button>
    </form>
  );
}

/* ================= LAGDETALJER ================= */

function TeamDetail({ year, team, season, types, actions, onClose }) {
  const extra = teamHandouts(season, team.id);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const newPassword = async () => {
    const pw = await actions.prompt({
      title: 'Nytt lösenord',
      message: <>Ledarna för <b>{team.name}</b> loggar in med det nya lösenordet från och med nu.</>,
      input: {
        label: 'Lösenord', placeholder: 'minst 4 tecken',
        validate: v => (v.length < 4 ? 'Lösenordet måste vara minst fyra tecken.' : ''),
      },
      okLabel: 'Byt lösenord',
    });
    if (!pw) return;
    const res = await actions.run(() => api.patch(`/seasons/${year}/teams/${team.id}`, { password: pw }), { ok: 'Lösenordet är bytt.' });
    if (res) await actions.load();
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
          <div>
            <div className="muted" style={{ fontSize: 13, fontWeight: 600 }}>Lagdetaljer · {year}</div>
            <TeamName year={year} team={team} actions={actions}
              style={{ margin: '3px 0 0', fontSize: 22, fontWeight: 800, letterSpacing: '-.015em' }} />
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>Ledare: {team.leadersLabel}</div>
            {extra.label && <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>Extra lotter från kansliet: <b>{extra.label}</b></div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {team.done
              ? <button className="badge-done" style={{ border: 'none', cursor: 'pointer', padding: '7px 12px', fontSize: 13 }} title="Markera som ej klar" onClick={() => actions.setDone(year, team.id, false)}>✓ Klar</button>
              : <button className="btn-ghost" style={{ fontSize: 13 }} onClick={() => actions.setDone(year, team.id, true)}>Markera klar</button>}
            <button className="btn-ghost" style={{ fontSize: 13 }} onClick={newPassword}>Nytt lösenord</button>
            <button className="close" onClick={onClose} aria-label="Stäng">✕</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 11, marginBottom: 18 }}>
          <div className="mini-kpi"><div className="kpi-label">Intjänat SIF</div><div className="v">{kr(team.club)}</div></div>
          <div className="mini-kpi soft"><div className="kpi-label green">Lagkassa</div><div className="v green">{kr(team.kassa)}</div></div>
          <div className="mini-kpi"><div className="kpi-label">Lotter</div><div className="v">{team.lots}</div></div>
          <div className="mini-kpi"><div className="kpi-label">Kalendrar</div><div className="v">{team.cal}</div></div>
          <div className="mini-kpi"><div className="kpi-label amb">Returer</div><div className="v amb">{team.ret}</div></div>
        </div>
        <div style={{ border: '1px solid var(--bd)', borderRadius: 12, overflow: 'hidden' }}>
          <div className="table-wrap" style={{ maxHeight: '46vh' }}>
            <PlayerTable team={team} types={types} compact
              onSold={(playerId, type, v) => actions.saveSold(year, team.id, playerId, type, v)}
              onFlags={(playerId, flags) => actions.savePlayer(year, team.id, playerId, flags)} />
          </div>
        </div>
      </div>
    </div>
  );
}
